import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  approveWorkspaceBooking,
  cancelWorkspaceBooking,
  listManagedWorkspaceBookings,
} from "../../../api/workspaceBookings";
import {
  activateWorkspaceLease,
  cancelWorkspaceLease,
  listManagedWorkspaceLeases,
  terminateWorkspaceLease,
} from "../../../api/workspaceLeases";
import { listManagedWorkspaces } from "../../../api/workspaces";
import { useAuth } from "../../../context/AuthContext";
import {
  formatWorkspaceDateTime,
  formatWorkspaceMoney,
  getWorkspaceDisplayName,
  titleCaseLabel,
} from "../../../utils/workspacePresentation";
import {
  getWorkspaceFinanceSnapshot,
  getWorkspacePaymentState,
  paymentToneClasses,
} from "../../../utils/workspaceBillingPresentation";
import {
  getMarketplaceOperatorPaymentsPath,
  getMarketplaceOperatorWorkspaceEditPath,
  getMarketplaceOperatorWorkspacesPath,
} from "../../../utils/marketplaceOperatorRoutes";
import RentalsShell, { rentalsHeroSecondaryActionClass } from "./RentalsShell";

const fieldClass =
  "h-11 rounded-xl border border-slate-300 px-4 text-sm text-slate-900 shadow-sm focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10";

function statusTone(status = "") {
  const normalized = String(status || "").toLowerCase();
  if (["approved", "confirmed", "active", "signed", "deposit_held"].includes(normalized)) {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (["cancelled", "canceled", "declined", "terminated", "ended", "disputed"].includes(normalized)) {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (["payment_required", "deposit_due", "payment_pending", "renewal_due", "ending_soon"].includes(normalized)) {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  return "border-amber-200 bg-amber-50 text-amber-700";
}

function metricValue(rows, statuses) {
  const allowed = new Set(statuses);
  return rows.filter((row) => allowed.has(row.agreementStatus?.key)).length;
}

function buildAgreementStatus(record = {}, kind = "request") {
  const rawStatus = String(record.status || "").toLowerCase();
  const financeState = String(
    record.finance_state_code ||
      record.invoice_metadata?.workspace_finance?.finance_state_code ||
      ""
  ).toLowerCase();
  const paymentStatus = String(record.payment_status || "").toLowerCase();

  if (record.dispute_open || financeState === "disputed") {
    return { key: "disputed", label: "Disputed", tone: "disputed" };
  }
  if (financeState === "deposit_held") {
    return { key: "deposit_held", label: "Deposit Held", tone: "deposit_held" };
  }
  if (["deposit_due", "payment_pending", "requires_payment"].includes(paymentStatus)) {
    return { key: "payment_required", label: "Payment Required", tone: "payment_required" };
  }
  if (["draft"].includes(rawStatus)) {
    return { key: "draft", label: "Draft", tone: "draft" };
  }
  if (["requested"].includes(rawStatus)) {
    return { key: "requested", label: "Requested", tone: "requested" };
  }
  if (["pending", "pending_approval"].includes(rawStatus)) {
    return { key: "pending_approval", label: "Pending Approval", tone: "pending_approval" };
  }
  if (["approved"].includes(rawStatus)) {
    return {
      key: kind === "request" ? "active" : "approved",
      label: kind === "request" ? "Active" : "Approved",
      tone: "approved",
    };
  }
  if (["confirmed", "active", "signed"].includes(rawStatus)) {
    return { key: "active", label: "Active", tone: "active" };
  }
  if (["renewal_due", "ending_soon"].includes(rawStatus)) {
    return { key: "ending_soon", label: "Ending Soon", tone: "ending_soon" };
  }
  if (["ended", "terminated", "completed", "payout_released"].includes(rawStatus)) {
    return { key: "ended", label: "Ended", tone: "ended" };
  }
  if (["cancelled", "canceled"].includes(rawStatus)) {
    return { key: "cancelled", label: "Cancelled", tone: "cancelled" };
  }
  if (["declined", "rejected"].includes(rawStatus)) {
    return { key: "declined", label: "Declined", tone: "declined" };
  }
  return {
    key: rawStatus || "requested",
    label: titleCaseLabel(record.status, kind === "request" ? "Requested" : "Draft"),
    tone: rawStatus || "requested",
  };
}

function matchesStatusFilter(statusInfo, rawStatus, filter) {
  if (!filter || filter === "all") return true;
  const normalized = String(filter || "").toLowerCase();
  const aliases = {
    pending: "pending_approval",
    confirmed: "active",
    signed: "active",
    renewal_due: "ending_soon",
    terminated: "ended",
    canceled: "cancelled",
  };
  return (
    statusInfo.key === (aliases[normalized] || normalized) ||
    String(rawStatus || "").toLowerCase() === normalized
  );
}

function getRequestKindLabel(statusInfo) {
  return ["active", "approved", "deposit_held", "payment_required", "ending_soon"].includes(
    statusInfo.key
  )
    ? "Rental Agreement"
    : "Rental Request";
}

function getTermLabel(row = {}) {
  if (row.term_length) return row.term_length;
  if (row.start_time || row.end_time) {
    return `${formatWorkspaceDateTime(row.start_time)} to ${formatWorkspaceDateTime(row.end_time)}`;
  }
  if (row.start_date || row.end_date) {
    return `${row.start_date || "TBD"}${row.end_date ? ` to ${row.end_date}` : " ongoing"}`;
  }
  return "Term to be confirmed";
}

export default function RentalAgreementsPage() {
  const { token, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const salonId =
    user?.salon_id ??
    (typeof window !== "undefined"
      ? Number(window.localStorage.getItem("salon_id")) || null
      : null);
  const [requests, setRequests] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState("");
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");

  const filters = useMemo(
    () => ({
      query: params.get("q") || "",
      status: params.get("status") || "all",
      workspaceId: params.get("workspaceId") || "",
    }),
    [params]
  );

  useEffect(() => {
    if (!token || !salonId) return undefined;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      listManagedWorkspaceBookings(token, salonId),
      listManagedWorkspaceLeases(token, salonId),
      listManagedWorkspaces(token, salonId),
    ])
      .then(([requestRows, agreementRows, workspaceRows]) => {
        if (!active) return;
        setRequests(Array.isArray(requestRows) ? requestRows : []);
        setAgreements(Array.isArray(agreementRows) ? agreementRows : []);
        setWorkspaces(Array.isArray(workspaceRows) ? workspaceRows : []);
      })
      .catch((err) => {
        if (active) setError(err.message || "Unable to load rental agreements.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [salonId, token]);

  const workspaceMap = useMemo(
    () => new Map(workspaces.map((workspace) => [workspace.id, workspace])),
    [workspaces]
  );

  const filteredRequests = useMemo(() => {
    return requests.map((request) => ({
      ...request,
      agreementStatus: buildAgreementStatus(request, "request"),
    })).filter((request) => {
      if (!matchesStatusFilter(request.agreementStatus, request.status, filters.status)) {
        return false;
      }
      if (filters.workspaceId && String(request.booth_id || "") !== String(filters.workspaceId)) {
        return false;
      }
      if (!filters.query) return true;
      const workspace = workspaceMap.get(request.booth_id);
      const finance = getWorkspaceFinanceSnapshot(request);
      const haystack = [
        request.renter_name,
        request.renter_email,
        request.marketplace_reference,
        finance.workspaceName,
        getWorkspaceDisplayName(workspace),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(filters.query.toLowerCase());
    });
  }, [filters, requests, workspaceMap]);

  const filteredAgreements = useMemo(() => {
    return agreements.map((agreement) => ({
      ...agreement,
      agreementStatus: buildAgreementStatus(agreement, "agreement"),
    })).filter((agreement) => {
      if (!matchesStatusFilter(agreement.agreementStatus, agreement.status, filters.status)) {
        return false;
      }
      if (filters.workspaceId && String(agreement.workspace_id || "") !== String(filters.workspaceId)) {
        return false;
      }
      if (!filters.query) return true;
      const workspace = workspaceMap.get(agreement.workspace_id);
      const haystack = [
        agreement.renter_name,
        agreement.renter_email,
        agreement.marketplace_reference,
        getWorkspaceDisplayName(workspace),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(filters.query.toLowerCase());
    });
  }, [agreements, filters, workspaceMap]);

  const metrics = useMemo(() => {
    const allRows = [...filteredRequests, ...filteredAgreements];
    const dueNow = filteredRequests.reduce(
      (sum, request) => sum + Number(getWorkspaceFinanceSnapshot(request).amountDueNow || 0),
      0
    );
    return {
      requested: metricValue(filteredRequests, ["requested", "pending_approval"]),
      active: metricValue(allRows, ["active", "approved"]),
      paymentRequired: metricValue(allRows, ["payment_required", "deposit_held"]),
      ending: metricValue(filteredAgreements, ["ending_soon"]),
      dueNow,
    };
  }, [filteredAgreements, filteredRequests]);

  const updateFilter = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setParams(next, { replace: true });
  };

  const updateRequest = async (requestId, action) => {
    if (!token) return;
    setActionId(`request-${requestId}`);
    setError("");
    setFlash("");
    try {
      const updated =
        action === "approve"
          ? await approveWorkspaceBooking(requestId, { reason: "Approved as a rental request." }, token)
          : await cancelWorkspaceBooking(requestId, { reason: "Declined from rental agreement review." }, token);
      setRequests((prev) => prev.map((request) => (request.id === requestId ? updated : request)));
      setFlash(
        action === "approve"
          ? "Rental request approved and converted to an active rental agreement."
          : "Rental request declined."
      );
    } catch (err) {
      setError(err.message || "Unable to update rental request.");
    } finally {
      setActionId("");
    }
  };

  const updateAgreement = async (agreementId, action) => {
    if (!token) return;
    setActionId(`agreement-${agreementId}`);
    setError("");
    setFlash("");
    try {
      const updated =
        action === "activate"
          ? await activateWorkspaceLease(agreementId, token)
          : action === "terminate"
          ? await terminateWorkspaceLease(agreementId, { reason: "Ended from agreement review." }, token)
          : await cancelWorkspaceLease(agreementId, { reason: "Cancelled from agreement review." }, token);
      setAgreements((prev) =>
        prev.map((agreement) => (agreement.id === agreementId ? updated : agreement))
      );
      setFlash(
        action === "activate"
          ? "Rental agreement activated."
          : action === "terminate"
          ? "Rental agreement ended."
          : "Rental agreement cancelled."
      );
    } catch (err) {
      setError(err.message || "Unable to update rental agreement.");
    } finally {
      setActionId("");
    }
  };

  return (
    <RentalsShell
      title="Rental Agreements"
      description="Review rental requests, active agreements, payment schedules, deposits, and agreement status from one workspace-centered operator view."
      actions={
        <div className="flex flex-wrap gap-3">
          <Link to={getMarketplaceOperatorWorkspacesPath()} className={rentalsHeroSecondaryActionClass}>
            Open Workspaces
          </Link>
          <Link to={getMarketplaceOperatorPaymentsPath()} className={rentalsHeroSecondaryActionClass}>
            Open Payments
          </Link>
        </div>
      }
    >
      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}
      {flash ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {flash}
        </div>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-4">
        {[
          ["Pending Approval", metrics.requested],
          ["Active Agreements", metrics.active],
          ["Payment Required", metrics.paymentRequired],
          ["Ending Soon", metrics.ending],
        ].map(([label, value]) => (
          <article key={label} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {label}
            </div>
            <div className="mt-3 text-3xl font-semibold text-slate-900">{value}</div>
          </article>
        ))}
      </div>

      <section className="rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="grid gap-4 border-b border-slate-200 px-6 py-5 lg:grid-cols-[1.2fr,0.9fr,0.9fr]">
          <input
            className={fieldClass}
            placeholder="Search renter, workspace, or reference"
            value={filters.query}
            onChange={(event) => updateFilter("q", event.target.value)}
          />
          <select
            className={fieldClass}
            value={filters.status}
            onChange={(event) => updateFilter("status", event.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="requested">Requested</option>
            <option value="pending">Pending Approval</option>
            <option value="approved">Approved</option>
            <option value="active">Active</option>
            <option value="payment_required">Payment Required</option>
            <option value="deposit_held">Deposit Held</option>
            <option value="ending_soon">Ending Soon</option>
            <option value="ended">Ended</option>
            <option value="cancelled">Cancelled</option>
            <option value="declined">Declined</option>
            <option value="disputed">Disputed</option>
          </select>
          <select
            className={fieldClass}
            value={filters.workspaceId}
            onChange={(event) => updateFilter("workspaceId", event.target.value)}
          >
            <option value="">All workspaces</option>
            {workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {getWorkspaceDisplayName(workspace)}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-5 px-6 py-6">
          {loading ? <p className="text-sm text-slate-600">Loading rental agreements...</p> : null}
          {!loading && !filteredRequests.length && !filteredAgreements.length ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm text-slate-600">
              No rental agreements match the current filters.
            </div>
          ) : null}

          {filteredRequests.map((request) => {
            const workspace = workspaceMap.get(request.booth_id);
            const status = String(request.status || "").toLowerCase();
            const finance = getWorkspaceFinanceSnapshot(request);
            const paymentState = getWorkspacePaymentState(request);
            const agreementStatus = request.agreementStatus;
            return (
              <article key={`request-${request.id}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                        {getRequestKindLabel(agreementStatus)}
                      </span>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(agreementStatus.tone)}`}>
                        {agreementStatus.label}
                      </span>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${paymentToneClasses(paymentState.tone)}`}>
                        {paymentState.label}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      {request.renter_name || request.renter_email || "Unnamed renter"}
                    </h2>
                    <p className="text-sm text-slate-600">
                      {finance.workspaceName || getWorkspaceDisplayName(workspace)} -{" "}
                      {formatWorkspaceDateTime(request.start_time)} to{" "}
                      {formatWorkspaceDateTime(request.end_time)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {status === "pending" ? (
                      <button
                        type="button"
                        disabled={actionId === `request-${request.id}`}
                        onClick={() => updateRequest(request.id, "approve")}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionId === `request-${request.id}` ? "Updating..." : "Approve Request"}
                      </button>
                    ) : null}
                    {!["cancelled", "declined"].includes(status) ? (
                      <button
                        type="button"
                        disabled={actionId === `request-${request.id}`}
                        onClick={() => updateRequest(request.id, "cancel")}
                        className="rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:border-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionId === `request-${request.id}` ? "Updating..." : "Decline Request"}
                      </button>
                    ) : null}
                    {workspace?.id ? (
                      <Link to={getMarketplaceOperatorWorkspaceEditPath(workspace.id)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400">
                        Open Workspace
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-4">
                  <div><span className="font-semibold">Term length:</span> {getTermLabel(request)}</div>
                  <div><span className="font-semibold">Payment schedule:</span> {titleCaseLabel(request.pricing_type || request.payment_schedule, "To be confirmed")}</div>
                  <div><span className="font-semibold">Rent:</span> {formatWorkspaceMoney(request.total_amount)}</div>
                  <div><span className="font-semibold">Deposit:</span> {request.deposit_amount ? formatWorkspaceMoney(request.deposit_amount) : "Not required"}</div>
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  {agreementStatus.key === "active"
                    ? "This approved request is shown as a rental agreement in MyBoothShare while legacy records remain internal."
                    : "Approval moves this request into the active rental agreement workflow."}
                </div>
              </article>
            );
          })}

          {filteredAgreements.map((agreement) => {
            const workspace = workspaceMap.get(agreement.workspace_id);
            const status = String(agreement.status || "").toLowerCase();
            const agreementStatus = agreement.agreementStatus;
            return (
              <article key={`agreement-${agreement.id}`} className="rounded-3xl border border-slate-200 bg-slate-50 p-5">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.22em] text-rose-500">
                        Rental Agreement
                      </span>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusTone(agreementStatus.tone)}`}>
                        {agreementStatus.label}
                      </span>
                      <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                        {titleCaseLabel(agreement.billing_interval || agreement.recurrence, "Monthly")}
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold text-slate-900">
                      {getWorkspaceDisplayName(workspace)}
                    </h2>
                    <p className="text-sm text-slate-600">
                      {getTermLabel(agreement)}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {status === "pending" ? (
                      <button
                        type="button"
                        disabled={actionId === `agreement-${agreement.id}`}
                        onClick={() => updateAgreement(agreement.id, "activate")}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionId === `agreement-${agreement.id}` ? "Updating..." : "Activate"}
                      </button>
                    ) : null}
                    {status === "active" ? (
                      <button
                        type="button"
                        disabled={actionId === `agreement-${agreement.id}`}
                        onClick={() => updateAgreement(agreement.id, "terminate")}
                        className="rounded-full border border-amber-300 px-4 py-2 text-sm font-semibold text-amber-700 hover:border-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionId === `agreement-${agreement.id}` ? "Updating..." : "End Agreement"}
                      </button>
                    ) : null}
                    {["pending", "draft", "approved", "signed", "renewal_due"].includes(status) ? (
                      <button
                        type="button"
                        disabled={actionId === `agreement-${agreement.id}`}
                        onClick={() => updateAgreement(agreement.id, "cancel")}
                        className="rounded-full border border-rose-300 px-4 py-2 text-sm font-semibold text-rose-700 hover:border-rose-400 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {actionId === `agreement-${agreement.id}` ? "Updating..." : "Cancel"}
                      </button>
                    ) : null}
                    {workspace?.id ? (
                      <Link to={getMarketplaceOperatorWorkspaceEditPath(workspace.id)} className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:border-slate-400">
                        Open Workspace
                      </Link>
                    ) : null}
                  </div>
                </div>
                <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-4">
                  <div><span className="font-semibold">Rent amount:</span> {formatWorkspaceMoney(agreement.amount)}</div>
                  <div><span className="font-semibold">Deposit:</span> {agreement.deposit_amount ? formatWorkspaceMoney(agreement.deposit_amount) : "Not required"}</div>
                  <div><span className="font-semibold">Payment schedule:</span> {titleCaseLabel(agreement.billing_interval || agreement.recurrence, "Monthly")}</div>
                  <div><span className="font-semibold">Renewal settings:</span> {agreement.auto_renew ? "Auto renews" : "Manual review"}</div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </RentalsShell>
  );
}
